import { ThreeItemKeys, ThreeItemTypes } from '../../../constants/three'
import { createMutation } from '@tanstack/svelte-query'
import useObjectQueries from './useObjectQueries'
import { Color, Group, Material, Mesh, MeshStandardMaterial, Object3D, Vector3, type LineBasicMaterial } from 'three'
import { get } from 'svelte/store'
import { storeScene } from '../../../stores/scene'
import { getPrinterId } from '../../../helpers/printer'
import { getColorFromTheme } from '../../../helpers/theme'
import queryClient from '../../../constants/query-client'
import { pick } from 'es-toolkit'
import { canvasSelection, pruneMissingFromMultiSelection } from '../../../stores/canvas-selection'
import { restoreOriginalMaterial } from '../../../helpers/selection-material'
import { splitObjectByContours } from '../../../helpers/three/split-mesh'
import { recenterGroupOnChildren } from '../../../helpers/three/recenter-group'
import { v4 as uuid } from 'uuid'
import { PrinterService } from '../../../services'

const useFileQueries = () => {
  const objectQueries = useObjectQueries()

  const setPrinterMaterialToFile = (file: Object3D, printer: Object3D) => {
    file.traverse((child) => {
      if ('material' in child && printer && !child.userData[ThreeItemKeys.TRANSIENT]) {
        (child.material as LineBasicMaterial | MeshStandardMaterial)?.color.set(new Color(
          getColorFromTheme(`--color-picker-${printer.userData[ThreeItemKeys.PRINTER_COLOR]}-solid`),
        ))
        child.userData.originalMaterial = (child.material as Material).toJSON()
      }
    })
  }

  const setPrinterLayerHeightToFile = (file: Object3D, printer: Object3D) => {
    const driver = PrinterService.getPrinterDriver(printer.userData[ThreeItemKeys.PRINTER_MODEL])
    file.userData[ThreeItemKeys.LAYER_HEIGHT] = driver.defaultLayerHeightMm
  }

  const resetFileMaterial = (file: Object3D) => {
    file.traverse((child) => {
      if ('material' in child && !child.userData[ThreeItemKeys.TRANSIENT]) {
        (child.material as LineBasicMaterial | MeshStandardMaterial)?.color.set(new Color('lightgray'))
        child.userData.originalMaterial = (child.material as Material).toJSON()
      }
    })
  }

  const replaceSeamMutation = createMutation(() => ({
    mutationFn: async ({ fileId, seamGroup, seamPosition }: { fileId: Object3D['uuid'], seamGroup?: Group, seamPosition?: Vector3 }) => {
      const file = objectQueries.getObject(fileId)

      // Cleanup existing seam
      const existingSeams: Object3D[] = []
      file.traverse((child) => {
        if (child.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.SEAM) {
          existingSeams.push(child)
        }
      })

      for (const existingSeam of existingSeams) {
        existingSeam.removeFromParent()
      }

      file.userData[ThreeItemKeys.SEAM] = seamPosition ? JSON.stringify(pick(seamPosition, ['x', 'y', 'z'])) : undefined

      if (seamGroup) {
        seamGroup.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.SEAM
        file.add(seamGroup)
      }
    },
    onSuccess: objectQueries.invalidateCanvas,
  }), () => queryClient)

  const assignToPrinter = (fileId: Object3D['uuid'], printerId?: Object3D['uuid']) => new Promise((resolve) => {
    let siblingsCount = 0
    const file = objectQueries.getObject(fileId)
    let printer: Object3D | undefined

    get(storeScene)?.traverse((item) => {
      if (getPrinterId(item) === printerId) {
        printer = item
      }
    })

    if (file.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === printerId) {
      return resolve(file)
    }

    get(storeScene)?.traverse((item) => {
      if (item.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === printerId) {
        siblingsCount += 1
      }
    })

    file.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] = printerId
    file.userData[ThreeItemKeys.PRINTING_ORDER] = siblingsCount + 1

    if (printer) {
      setPrinterMaterialToFile(file, printer)
      setPrinterLayerHeightToFile(file, printer)
    }
    else {
      resetFileMaterial(file)
    }

    resolve(file)
  })

  const assignToPrinterMutation = createMutation(() => ({
    mutationFn: ({ fileId, printerId }: { fileId: Object3D['uuid'], printerId?: Object3D['uuid'] }) =>
      assignToPrinter(fileId, printerId),
    onSuccess: objectQueries.invalidateCanvas,
  }), () => queryClient)

  const removeMutation = createMutation(() => ({
    mutationFn: (objectUuid: Object3D['uuid']) => new Promise((resolve) => {
      const object = objectQueries.getObject(objectUuid) as Group | Mesh
      const selectedObject = get(canvasSelection)
      let parentGroup: Group | undefined = undefined

      object.traverseAncestors((ancestor: Object3D) => {
        if (
          ancestor.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.GROUP
          && ancestor.userData[ThreeItemKeys.GROUP_ID] === object.userData[ThreeItemKeys.ASSIGNED_GROUP_ID]
        ) {
          if (!parentGroup) {
            parentGroup = ancestor as Group
          }
        }
      })

      if (selectedObject) {
        // The current selection can be `object` itself, a descendant of it
        // (rare), or an ancestor (e.g. the parent group stands in for a solo
        // child's selection) - any of those need clearing.
        let selectionAffected = selectedObject.uuid === object.uuid
        object.traverse((child) => {
          if (child.uuid === selectedObject.uuid) selectionAffected = true
        })
        object.traverseAncestors((ancestor) => {
          if (ancestor.uuid === selectedObject.uuid) selectionAffected = true
        })

        if (selectionAffected) {
          restoreOriginalMaterial(selectedObject)
          canvasSelection.set(undefined)
        }
      }

      object.removeFromParent()

      if (parentGroup) {
        const groupParent = parentGroup as Group

        if (groupParent.children.length === 0) {
          groupParent.removeFromParent()
        }
        else {
          recenterGroupOnChildren(groupParent)
        }
      }
      resolve(true)
    }),
    onSuccess: () => {
      pruneMissingFromMultiSelection()
      objectQueries.invalidateCanvas()
    },
  }), () => queryClient)

  const reorderFileMutation = createMutation(() => ({
    mutationFn: ({ objectUuid, order }: { objectUuid: Object3D['uuid'], order: number }) => new Promise((resolve) => {
      const file = objectQueries.getObject(objectUuid)
      file.userData[ThreeItemKeys.PRINTING_ORDER] = order
      resolve(file)
    }),
    onSuccess: objectQueries.invalidateCanvas,
  }), () => queryClient)

  /**
   * Split a file into multiple meshes based on contour analysis
   * This replaces the original file with multiple split files
   */
  const splitByContoursMutation = createMutation(() => ({
    mutationFn: (fileId: Object3D['uuid']) => new Promise((resolve) => {
      const scene = get(storeScene)
      const file = objectQueries.getObject(fileId)

      // Get the parent group of the file
      let parentGroup: Group | undefined
      file.traverseAncestors((ancestor) => {
        if (
          ancestor.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.GROUP
          && ancestor.userData[ThreeItemKeys.GROUP_ID] === file.userData[ThreeItemKeys.ASSIGNED_GROUP_ID]
        ) {
          if (!parentGroup) {
            parentGroup = ancestor as Group
          }
        }
      })

      // Split the object
      const splitGroup = splitObjectByContours(file)

      // IMPORTANT: Copy children array before modifying it
      // (adding to another parent removes from current parent)
      const splitChildren = [...splitGroup.children]
      const totalParts = splitChildren.length

      // If only one part (or no parts), nothing to split - keep original
      if (totalParts <= 1) {
        resolve(file)
        return
      }

      // Get the base name without extension for display
      const originalName = file.userData[ThreeItemKeys.FILE_NAME] || file.name || 'file'

      // Set up each split child with proper userData
      const groupId = parentGroup?.userData[ThreeItemKeys.GROUP_ID] || uuid()
      splitChildren.forEach((child, index) => {
        const partName = `${originalName} (${index + 1}/${totalParts})`

        // Handle both Mesh and Group children (DXF3D returns Groups)
        if (child instanceof Mesh || child instanceof Group) {
          child.userData = {
            ...file.userData,
            [ThreeItemKeys.FILE_ID]: uuid(),
            [ThreeItemKeys.FILE_NAME]: partName,
            [ThreeItemKeys.ASSIGNED_GROUP_ID]: groupId,
          }
          child.name = partName
        }
      })

      // Check if original file was assigned to a printer
      const assignedPrinterId = file.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID]

      // Remove the original file
      file.removeFromParent()

      // Add split children to the parent group or scene
      if (parentGroup) {
        splitChildren.forEach((child) => {
          // The child already has the correct local position (copied from original)
          // Just add it to the parent group
          parentGroup!.add(child)
        })
      }
      else {
        scene?.add(splitGroup)
      }

      // If the original file was assigned to a printer, reassign printing orders
      if (assignedPrinterId) {
        const filesOnPrinter: Object3D[] = []
        scene?.traverse((item) => {
          if (
            item.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] === assignedPrinterId
            && item.userData[ThreeItemKeys.ITEM_TYPE] === ThreeItemTypes.FILE
          ) {
            filesOnPrinter.push(item)
          }
        })

        // Sort by current printing order and reassign sequential orders
        filesOnPrinter.sort((a, b) => {
          const orderA = a.userData[ThreeItemKeys.PRINTING_ORDER] || 0
          const orderB = b.userData[ThreeItemKeys.PRINTING_ORDER] || 0
          return orderA - orderB
        })

        filesOnPrinter.forEach((item, index) => {
          item.userData[ThreeItemKeys.PRINTING_ORDER] = index + 1
        })
      }

      // Clear selection
      const currentSelection = get(canvasSelection)
      if (currentSelection) restoreOriginalMaterial(currentSelection)
      canvasSelection.set(undefined)

      resolve(splitGroup)
    }),
    onSuccess: () => {
      pruneMissingFromMultiSelection()
      // Force refetch to update the sidebar immediately
      queryClient.refetchQueries({ queryKey: ['canvas'] })
    },
  }), () => queryClient)

  return {
    ...objectQueries,
    assignToPrinterMutation,
    reorderFileMutation,
    replaceSeamMutation,
    removeMutation,
    splitByContoursMutation,
  }
}

export default useFileQueries
